<template>
  <div class="mx-auto max-w-[864px] overflow-y-auto py-7 text-center">
    <Slider
      v-if="hasSizes"
      ref="slider"
      :deselect-item="deselectItem"
      :items="sizes"
      @update:model-value="(val) => selectDimension(val)"
    >
      <template #content="{ item }">
        <div class="relative">
          <div v-if="item.type == 'custom'" class="absolute -top-1 right-0">
            <DropdownActions
              trigger-icon-prefix="bold/DotsThreeVertical"
              :actions="[
                {
                  label: t('delete'),
                  destructive: true,
                  handler: () => deleteDimension(item),
                },
              ]"
            />
          </div>
          <Icon class="mx-auto mb-2 max-w-[32px]" src="light/cube_transparent" :size="ComponentSize.xl4" />
          <p class="text-body-default-m">
            {{ item.name }}
          </p>
          <p class="text-body-default">{{ item.length }} x {{ item.width }} x {{ item.height }}</p>
        </div>
      </template>
    </Slider>
    <Slider v-else :items="Array.from({ length: 5 })" :deselect-item="false">
      <template #item>
        <div class="flex flex-col items-center gap-2">
          <Bone class="shadow-inset-00-01 mx-auto h-28 min-w-45 rounded-lg" type="light" />
        </div>
      </template>
    </Slider>
    <div class="mt-5 mb-3 flex w-full items-center gap-4">
      <InputValueXL
        v-model="length"
        type="number"
        suffix="cm"
        required
        min="1"
        :label="t('length')"
        data-testid="length-input"
        @update:model-value="deselectItem = !deselectItem"
      />
      <Icon class="mt-6" src="X" :size="ComponentSize.lg" />
      <InputValueXL
        v-model="width"
        type="number"
        suffix="cm"
        required
        min="1"
        :label="t('width')"
        data-testid="width-input"
        @update:model-value="deselectItem = !deselectItem"
      />
      <Icon class="mt-6" src="X" :size="ComponentSize.lg" />
      <InputValueXL
        v-model="height"
        type="number"
        suffix="cm"
        required
        min="1"
        :label="t('height')"
        data-testid="height-input"
        @update:model-value="deselectItem = !deselectItem"
      />
    </div>
    <Button
      class="mx-auto mb-5 w-full max-w-max px-1"
      type="button"
      :disabled="!length || !height || !width"
      :variant="ButtonVariant.Secondary"
      @click="saveSizeOpen = true"
      >{{ t("save_as_standard") }}</Button
    >
    <Divider class="my-5" />
    <Slider
      v-if="weights"
      ref="slider"
      :deselect-item="deselectItem"
      :items="weights"
      @update:model-value="(val) => selectWeight(val)"
    >
      <template #content="{ item }">
        <div class="relative">
          <div v-if="item.custom" class="absolute -top-1 right-0">
            <DropdownActions
              trigger-icon-prefix="bold/DotsThreeVertical"
              :actions="[
                {
                  label: t('delete'),
                  destructive: true,
                  handler: () => deleteWeight(item),
                },
              ]"
            />
          </div>
          <p class="text-body-default-m mx-auto max-w-32.5 truncate">{{ item.value }} {{ item.unit }}</p>
        </div>
      </template>
    </Slider>
    <div class="mt-5 flex flex-col items-center gap-2">
      <InputValueXL
        v-model="formattedWeight"
        :suffix="displayWeightUnit"
        required
        min="1"
        :label="t('weight')"
        data-testid="weight-input"
      />
      <Button class="w-fit" :variant="ButtonVariant.Tertiary" type="button" @click="swapDisplayWeightUnit">
        <template #prefix>
          <Icon src="bold/ArrowsDownUp" />
        </template>
        {{ weightMessage }}
      </Button>
      <Button
        class="mx-auto w-full max-w-max px-1"
        type="button"
        :disabled="!formattedWeight"
        :variant="ButtonVariant.Secondary"
        @click="saveWeight"
        >{{ t("save_as_standard") }}</Button
      >
    </div>
    <Divider class="my-5" />
    <button
      type="button"
      class="border-bdr-subtle bg-bgr-default mx-auto flex h-14 w-full cursor-pointer items-center gap-2 rounded-xl border px-5"
      :aria-pressed="has_dangerous_goods"
      @click="has_dangerous_goods = !has_dangerous_goods"
    >
      <Icon src="Warning" filled class="text-fgr-critical" :size="ComponentSize.xl" />
      <p class="text-body-large-m text-fgr-critical">{{ t("dangerous_goods") }}</p>
      <p class="text-body-large text-fgr-secondary">{{ t("shipment_has_dangerous_goods") }}</p>
      <Toggle v-model="has_dangerous_goods" class="ml-auto" @click.stop="" />
    </button>
  </div>
  <Modal ref="modal" v-model:open="saveSizeOpen" size="fixed" width="342px">
    <div class="flex flex-col gap-4">
      <InputText v-model="customSizeName" :label="t('name_new_size')" :placeholder="t('size_name')" />
      <p class="text-body-large mx-auto flex items-center gap-2 font-medium">
        L:{{ length }}cm
        <Icon src="X" :size="ComponentSize.sm" />
        B:{{ width }}cm
        <Icon src="X" :size="ComponentSize.sm" />
        H:{{ height }}cm
      </p>
    </div>
    <template #footer>
      <ModalFooter
        :confirm-text="`${t('save')} ${t('size').toLowerCase()}`"
        :disabled="!customSizeName"
        @cancel="saveSizeOpen = false"
        @confirm="saveSize"
      />
    </template>
  </Modal>
</template>

<script setup lang="ts">
  interface Size {
    name: string;
    height: number;
    length: number;
    width: number;
  }

  const { t } = useI18n();

  const customSizeName = ref("");

  const saveSizeOpen = ref(false);

  const deselectItem = ref(false);

  const slider = ref(null);

  const sizes = useState("newshipment_dimensions", () => []);

  const weights = useState("newshipment_weights", () => []);

  const hasSizes = computed(() => sizes.value.length > 0);

  const displayWeightUnit = ref(usePreferredWeightUnit());

  const fetchWeights = async () => {
    const response = await homeFetch("weight-presets");
    if (response?.data) {
      weights.value = response.data;
    }
  };

  const fetchDimensions = async () => {
    const startTime = Date.now();
    const waitTime = 350;
    const response = await homeFetch("dimensions");

    // Calculate time elapsed
    const timeElapsed = Date.now() - startTime;

    // If less than waitTime has passed, wait for the remaining time
    if (timeElapsed < waitTime) {
      await new Promise((resolve) => setTimeout(resolve, waitTime - timeElapsed));
    }

    if (response?.data) sizes.value = response.data;
    return response;
  };

  const saveWeight = () => {
    const weightData = {
      value: formattedWeight.value,
      unit: displayWeightUnit.value,
    };
    homeFetch("weight-presets", {
      method: "POST",
      body: weightData,
    })
      .then((res) => {
        const newId = res?.data?.id;
        fetchWeights().then(() => {
          const weightIndex = weights.value?.findIndex((w) => w.id === newId);
          slider.value?.updateSelection(res?.data, weightIndex, true);
        });
      })
      .then(() => {
        toastAdd({
          text: t("weight_saved"),
        });
      });
  };

  const saveSize = () => {
    const size = {
      height: height.value,
      length: length.value,
      width: width.value,
      name: customSizeName.value,
    };
    homeFetch("dimensions", {
      method: "POST",
      body: size,
    })
      .then((res) => {
        const newId = res?.data?.id;
        fetchDimensions().then(() => {
          const dimensionIndex = sizes.value?.findIndex((s) => s.id === newId);

          slider.value?.updateSelection(res?.data, dimensionIndex, true);
        });
      })
      .then(() => {
        toastAdd({
          text: t("dimension_saved"),
        });
        saveSizeOpen.value = false;
        customSizeName.value = "";
      });
  };

  const selectDimension = (size: Size) => {
    length.value = size?.length;
    height.value = size?.height;
    width.value = size?.width || 10;
  };

  const selectWeight = (weight: { value: number; unit: string }) => {
    formattedWeight.value = convertWeight(weight.value, weight.unit, displayWeightUnit.value);
  };

  const deleteWeight = (weight: { id: number }) => {
    homeFetch(`weight-presets/${weight.id}`, {
      method: "DELETE",
    }).then(() => {
      weights.value = weights.value.filter((w: { id: number }) => w.id !== weight.id);
      toastAdd({
        text: t("weight_deleted"),
      });
    });
  };

  const deleteDimension = (size: Size) => {
    homeFetch(`dimensions/${size.id}`, {
      method: "DELETE",
    }).then(() => {
      sizes.value = sizes.value.filter((s: Size) => s.id !== size.id);
    });
  };

  const { value: height } = useField(() => "height", undefined, {
    syncVModel: true,
  });

  const { value: width } = useField(() => "width", undefined, {
    syncVModel: true,
  });

  const { value: length } = useField(() => "length", undefined, {
    syncVModel: true,
  });

  const { value: weight } = useField<number>(() => "weight", undefined, {
    syncVModel: true,
  });

  const { value: has_dangerous_goods } = useField<boolean>(() => "has_dangerous_goods", undefined, {
    syncVModel: true,
  });

  const formattedWeight = computed({
    get: () => {
      if (!weight.value) return "";

      return convertWeight(weight.value, "g", displayWeightUnit.value);
    },
    set: (val) => {
      // This is where the weight value is set and we must always convert it to grams
      weight.value = convertWeight(val, displayWeightUnit.value, "g");
    },
  });

  const weightMessage = computed(() => {
    if (useIsPreferredWeightUnitImperial().value) {
      if (displayWeightUnit.value === "oz") return useFormatWeight(weight.value || 0, { unit: "lb" });
      return useFormatWeight(weight.value || 0, { unit: "oz" });
    }

    //convert to best metric unit based on weight and default unit
    switch (displayWeightUnit.value) {
      case "g":
        return useFormatWeight(weight.value || 0, { unit: "kg" });
      case "kg":
        return useFormatWeight(weight.value || 0, { unit: "g" });
      default:
        return "";
    }
  });

  const swapDisplayWeightUnit = () => {
    if (isWeightUnitImperial(displayWeightUnit.value)) {
      displayWeightUnit.value = displayWeightUnit.value === "oz" ? "lb" : "oz";
    } else displayWeightUnit.value = displayWeightUnit.value === "g" ? "kg" : "g";
  };

  onBeforeMount(() => {
    fetchDimensions();
    fetchWeights();
  });
</script>
